import React, { useEffect, Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import Header from './components/Header';
import Footer from './components/Footer';
import NoiseOverlay from './components/NoiseOverlay';

// Eagerly loaded (above the fold)
import Home from './pages/Home';

// Lazy loaded — Pages
const ManifestoPage = lazy(() => import('./pages/ManifestoPage'));
const Locations = lazy(() => import('./pages/Locations'));
const NotFoundPage = lazy(() => import('./pages/NotFoundPage'));
const AboutPage = lazy(() => import('./pages/AboutPage'));
const CaseStudiesPage = lazy(() => import('./pages/CaseStudiesPage'));
const ContactPage = lazy(() => import('./pages/ContactPage'));
const ProcessPage = lazy(() => import('./pages/ProcessPage'));

// Lazy loaded — Service pages
const PerformanceMarketing = lazy(() => import('./pages/services/PerformanceMarketing'));
const PPC = lazy(() => import('./pages/services/PPC'));
const SEO = lazy(() => import('./pages/services/SEO'));
const SocialMedia = lazy(() => import('./pages/services/SocialMedia'));
const ConversionOptimization = lazy(() => import('./pages/services/ConversionOptimization'));
const PerformanceAnalytics = lazy(() => import('./pages/services/PerformanceAnalytics'));

// Lazy loaded — Tool pages
const AdSpendEstimatorPage = lazy(() => import('./pages/tools/AdSpendEstimatorPage'));
const ROICalculatorPage = lazy(() => import('./pages/tools/ROICalculatorPage'));
const ROASCalculatorPage = lazy(() => import('./pages/tools/ROASCalculatorPage'));
const ROICalculatorLanding = lazy(() => import('./src/pages/tools/ROICalculatorLanding'));
const AdSpendEstimatorLanding = lazy(() => import('./src/pages/tools/AdSpendEstimatorLanding'));
const ROASCalculatorLanding = lazy(() => import('./src/pages/tools/ROASCalculatorLanding'));

// Lazy loaded — Location pages
const Kochi = lazy(() => import('./pages/locations/Kochi'));
const Ernakulam = lazy(() => import('./pages/locations/Ernakulam'));
const Mumbai = lazy(() => import('./pages/locations/Mumbai'));
const Delhi = lazy(() => import('./pages/locations/Delhi'));
const Bangalore = lazy(() => import('./pages/locations/Bangalore'));
const Chennai = lazy(() => import('./pages/locations/Chennai'));
const Hyderabad = lazy(() => import('./pages/locations/Hyderabad'));
const Pune = lazy(() => import('./pages/locations/Pune'));
const Ahmedabad = lazy(() => import('./pages/locations/Ahmedabad'));
const Kolkata = lazy(() => import('./pages/locations/Kolkata'));
const Dubai = lazy(() => import('./pages/locations/Dubai'));
const AbuDhabi = lazy(() => import('./pages/locations/AbuDhabi'));
const Riyadh = lazy(() => import('./pages/locations/Riyadh'));
const Jeddah = lazy(() => import('./pages/locations/Jeddah'));
const Dammam = lazy(() => import('./pages/locations/Dammam'));
const Charlotte = lazy(() => import('./pages/locations/Charlotte'));
const Columbus = lazy(() => import('./pages/locations/Columbus'));
const Copenhagen = lazy(() => import('./pages/locations/Copenhagen'));
const Dublin = lazy(() => import('./pages/locations/Dublin'));
const Jakarta = lazy(() => import('./pages/locations/Jakarta'));
const KualaLumpur = lazy(() => import('./pages/locations/KualaLumpur'));
const KochiPerformanceMarketing = lazy(() => import('./pages/locations/KochiPerformanceMarketing'));
const MumbaiPerformanceMarketing = lazy(() => import('./pages/locations/MumbaiPerformanceMarketing'));
const DubaiPerformanceMarketing = lazy(() => import('./pages/locations/DubaiPerformanceMarketing'));
const ServiceLocationPage = lazy(() => import('./pages/ServiceLocationPage'));

// Additional location pages (lazy)
const NewYork = lazy(() => import('./pages/locations/NewYork'));
const London = lazy(() => import('./pages/locations/London'));
const Singapore = lazy(() => import('./pages/locations/Singapore'));
const LosAngeles = lazy(() => import('./pages/locations/LosAngeles'));
const Chicago = lazy(() => import('./pages/locations/Chicago'));
const Houston = lazy(() => import('./pages/locations/Houston'));
const SanFrancisco = lazy(() => import('./pages/locations/SanFrancisco'));
const Seattle = lazy(() => import('./pages/locations/Seattle'));
const Bangkok = lazy(() => import('./pages/locations/Bangkok'));
const HongKong = lazy(() => import('./pages/locations/HongKong'));
const Tokyo = lazy(() => import('./pages/locations/Tokyo'));
const Seoul = lazy(() => import('./pages/locations/Seoul'));
const Amsterdam = lazy(() => import('./pages/locations/Amsterdam'));
const Berlin = lazy(() => import('./pages/locations/Berlin'));
const Paris = lazy(() => import('./pages/locations/Paris'));
const Madrid = lazy(() => import('./pages/locations/Madrid'));
const Rome = lazy(() => import('./pages/locations/Rome'));
const Prague = lazy(() => import('./pages/locations/Prague'));
const Vienna = lazy(() => import('./pages/locations/Vienna'));
const Warsaw = lazy(() => import('./pages/locations/Warsaw'));
const Stockholm = lazy(() => import('./pages/locations/Stockholm'));
const Dallas = lazy(() => import('./pages/locations/Dallas'));
const Austin = lazy(() => import('./pages/locations/Austin'));
const SanAntonio = lazy(() => import('./pages/locations/SanAntonio'));
const SanDiego = lazy(() => import('./pages/locations/SanDiego'));
const SanJose = lazy(() => import('./pages/locations/SanJose'));
const Denver = lazy(() => import('./pages/locations/Denver'));
const Phoenix = lazy(() => import('./pages/locations/Phoenix'));
const Philadelphia = lazy(() => import('./pages/locations/Philadelphia'));
const Indianapolis = lazy(() => import('./pages/locations/Indianapolis'));
const Jacksonville = lazy(() => import('./pages/locations/Jacksonville'));
const FortWorth = lazy(() => import('./pages/locations/FortWorth'));
const WashingtonDC = lazy(() => import('./pages/locations/WashingtonDC'));

const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
};

const LoadingFallback = () => (
  <div className="min-h-screen flex items-center justify-center bg-brand-black">
    <div className="text-brand-red font-mono text-sm animate-pulse">
      LOADING...
    </div>
  </div>
);

const App: React.FC = () => {
  
  useEffect(() => {
    console.log("%c DISRUPT NEXT // SYSTEM READY", "background: #ff0000; color: #000; font-size: 20px; font-weight: bold; padding: 10px;");
    document.dispatchEvent(new Event('prerender-ready'));
  }, []);

  return (
    <HelmetProvider>
      <Router>
        <ScrollToTop />
        <div className="bg-brand-black min-h-screen text-brand-white selection:bg-brand-red selection:text-black">
          <NoiseOverlay />
          <Header />
          
          <Suspense fallback={<LoadingFallback />}>
            <Routes>
              {/* Main pages */}
              <Route path="/" element={<Home />} />
              <Route path="/manifesto" element={<ManifestoPage />} />
              <Route path="/locations" element={<Locations />} />
              
              {/* About & Company */}
              <Route path="/about" element={<AboutPage />} />
              <Route path="/about/process" element={<ProcessPage />} />
              <Route path="/about/e3-methodology" element={<ManifestoPage />} />
              
              {/* Results */}
              <Route path="/results" element={<CaseStudiesPage />} />
              <Route path="/results/case-studies" element={<CaseStudiesPage />} />
              <Route path="/results/testimonials" element={<CaseStudiesPage />} />
              <Route path="/results/industries" element={<CaseStudiesPage />} />
              
              {/* Services */}
              <Route path="/services" element={<PPC />} />
              <Route path="/services/ppc" element={<PPC />} />
              <Route path="/services/seo" element={<SEO />} />
              <Route path="/services/social-media" element={<SocialMedia />} />
              <Route path="/services/conversion-optimization" element={<ConversionOptimization />} />
              <Route path="/services/analytics" element={<PerformanceAnalytics />} />
              <Route path="/services/performance-marketing" element={<PerformanceMarketing />} />
              
              {/* Contact */}
              <Route path="/contact" element={<ContactPage />} />
              <Route path="/deploy" element={<ContactPage />} />
              
              {/* Location routes — India */}
              <Route path="/kochi" element={<Kochi />} />
              <Route path="/ernakulam" element={<Ernakulam />} />
              <Route path="/mumbai" element={<Mumbai />} />
              <Route path="/delhi" element={<Delhi />} />
              <Route path="/bangalore" element={<Bangalore />} />
              <Route path="/chennai" element={<Chennai />} />
              <Route path="/hyderabad" element={<Hyderabad />} />
              <Route path="/pune" element={<Pune />} />
              <Route path="/ahmedabad" element={<Ahmedabad />} />
              <Route path="/kolkata" element={<Kolkata />} />
              
              {/* Location routes — Middle East */}
              <Route path="/dubai" element={<Dubai />} />
              <Route path="/abu-dhabi" element={<AbuDhabi />} />
              <Route path="/riyadh" element={<Riyadh />} />
              <Route path="/jeddah" element={<Jeddah />} />
              <Route path="/dammam" element={<Dammam />} />
              
              {/* Location routes — US */}
              <Route path="/new-york" element={<NewYork />} />
              <Route path="/los-angeles" element={<LosAngeles />} />
              <Route path="/chicago" element={<Chicago />} />
              <Route path="/houston" element={<Houston />} />
              <Route path="/san-francisco" element={<SanFrancisco />} />
              <Route path="/seattle" element={<Seattle />} />
              <Route path="/dallas" element={<Dallas />} />
              <Route path="/austin" element={<Austin />} />
              <Route path="/san-antonio" element={<SanAntonio />} />
              <Route path="/san-diego" element={<SanDiego />} />
              <Route path="/san-jose" element={<SanJose />} />
              <Route path="/denver" element={<Denver />} />
              <Route path="/phoenix" element={<Phoenix />} />
              <Route path="/philadelphia" element={<Philadelphia />} />
              <Route path="/charlotte" element={<Charlotte />} />
              <Route path="/columbus" element={<Columbus />} />
              <Route path="/indianapolis" element={<Indianapolis />} />
              <Route path="/jacksonville" element={<Jacksonville />} />
              <Route path="/fort-worth" element={<FortWorth />} />
              <Route path="/washington-dc" element={<WashingtonDC />} />
              
              {/* Location routes — Europe */}
              <Route path="/london" element={<London />} />
              <Route path="/amsterdam" element={<Amsterdam />} />
              <Route path="/berlin" element={<Berlin />} />
              <Route path="/paris" element={<Paris />} />
              <Route path="/madrid" element={<Madrid />} />
              <Route path="/rome" element={<Rome />} />
              <Route path="/prague" element={<Prague />} />
              <Route path="/vienna" element={<Vienna />} />
              <Route path="/warsaw" element={<Warsaw />} />
              <Route path="/stockholm" element={<Stockholm />} />
              <Route path="/copenhagen" element={<Copenhagen />} />
              <Route path="/dublin" element={<Dublin />} />
              
              {/* Location routes — Asia */}
              <Route path="/singapore" element={<Singapore />} />
              <Route path="/bangkok" element={<Bangkok />} />
              <Route path="/hong-kong" element={<HongKong />} />
              <Route path="/tokyo" element={<Tokyo />} />
              <Route path="/seoul" element={<Seoul />} />
              <Route path="/jakarta" element={<Jakarta />} />
              <Route path="/kuala-lumpur" element={<KualaLumpur />} />
              
              {/* Service + location routes */}
              <Route path="/performance-marketing-agency-kochi" element={<KochiPerformanceMarketing />} />
              <Route path="/performance-marketing-agency-mumbai" element={<MumbaiPerformanceMarketing />} />
              <Route path="/performance-marketing-agency-dubai" element={<DubaiPerformanceMarketing />} />
              <Route path="/services/:service/locations/:city" element={<ServiceLocationPage />} />
              
              {/* Tool routes */}
              <Route path="/tools" element={<ROICalculatorPage />} />
              <Route path="/tools/ad-spend-estimator" element={<AdSpendEstimatorPage />} />
              <Route path="/tools/roi-calculator" element={<ROICalculatorPage />} />
              <Route path="/tools/roas-calculator" element={<ROASCalculatorPage />} />
              <Route path="/tools/roi-calculator-landing" element={<ROICalculatorLanding />} />
              <Route path="/tools/ad-spend-estimator-landing" element={<AdSpendEstimatorLanding />} />
              <Route path="/tools/roas-calculator-landing" element={<ROASCalculatorLanding />} />

              {/* 404 catch-all */}
              <Route path="/404" element={<NotFoundPage />} />
              <Route path="*" element={<NotFoundPage />} />
            </Routes>
          </Suspense>
          
          <Footer />
        </div>
      </Router>
    </HelmetProvider>
  );
};

export default App;
